/**
 * Spanish, French, or Japanese UI from the machine time zone, then from the
 * browser language. GitHub Pages does not expose the client IP, so the IANA
 * time zone is the location signal.
 *
 * A Japanese browser language is used even outside Japan. An English browser
 * in a Spanish, French, or Japanese time zone keeps that zone's language.
 * French covers France, Monaco, Belgium, Luxembourg, French overseas
 * territories, and countries where French is an official language and the
 * time zone is not shared with a non-French majority.
 */

const SPANISH_REGION_TIME_ZONES = new Set([
  'Europe/Madrid',
  'Atlantic/Canary',
  'Africa/Ceuta',
  'America/Buenos_Aires',
  'America/Catamarca',
  'America/Cordoba',
  'America/Jujuy',
  'America/Mendoza',
  'America/Rosario',
  'America/Mexico_City',
  'America/Cancun',
  'America/Merida',
  'America/Monterrey',
  'America/Mazatlan',
  'America/Chihuahua',
  'America/Bahia_Banderas',
  'America/Tijuana',
  'America/Hermosillo',
  'America/Ojinaga',
  'America/Matamoros',
  'America/Ciudad_Juarez',
  'America/Guatemala',
  'America/El_Salvador',
  'America/Tegucigalpa',
  'America/Managua',
  'America/Costa_Rica',
  'America/Panama',
  'America/Bogota',
  'America/Guayaquil',
  'Pacific/Galapagos',
  'America/Lima',
  'America/La_Paz',
  'America/Santiago',
  'America/Coyhaique',
  'America/Punta_Arenas',
  'Pacific/Easter',
  'America/Asuncion',
  'America/Montevideo',
  'America/Caracas',
  'America/Havana',
  'America/Santo_Domingo',
  'America/Puerto_Rico',
  'America/Noronha',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Araguaina',
  'America/Maceio',
  'America/Bahia',
  'America/Sao_Paulo',
  'America/Santarem',
  'America/Manaus',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Rio_Branco',
  'America/Eirunepe'
]);

const FRENCH_REGION_TIME_ZONES = new Set([
  'Europe/Paris',
  'Europe/Monaco',
  'Europe/Brussels',
  'Europe/Luxembourg',
  'America/Martinique',
  'America/Guadeloupe',
  'America/Marigot',
  'America/St_Barthelemy',
  'America/Cayenne',
  'America/Miquelon',
  'America/Port-au-Prince',
  'Indian/Reunion',
  'Indian/Mayotte',
  'Indian/Antananarivo',
  'Indian/Comoro',
  'Indian/Mahe',
  'Pacific/Tahiti',
  'Pacific/Marquesas',
  'Pacific/Gambier',
  'Pacific/Noumea',
  'Pacific/Wallis',
  'Pacific/Efate',
  'Africa/Abidjan',
  'Africa/Dakar',
  'Africa/Bamako',
  'Africa/Ouagadougou',
  'Africa/Lome',
  'Africa/Porto-Novo',
  'Africa/Niamey',
  'Africa/Conakry',
  'Africa/Douala',
  'Africa/Libreville',
  'Africa/Brazzaville',
  'Africa/Kinshasa',
  'Africa/Lubumbashi',
  'Africa/Bangui',
  'Africa/Ndjamena',
  'Africa/Djibouti',
  'Africa/Bujumbura',
  'Africa/Kigali'
]);

const JAPANESE_REGION_TIME_ZONES = new Set([
  'Asia/Tokyo'
]);

const DOCUMENT_COPY = {
  en: {
    lang: 'en',
    title: 'deChess — decentralized peer-to-peer chess',
    description: 'Play verifiable peer-to-peer chess with no account, advertising, or central game server. English, Spanish, French, and Japanese.'
  },
  es: {
    lang: 'es',
    title: 'deChess — ajedrez entre pares, sin servidor',
    description: 'Juega ajedrez verificable entre navegadores, sin cuenta, anuncios ni servidor central.'
  },
  fr: {
    lang: 'fr',
    title: 'deChess — échecs entre pairs, sans serveur',
    description: 'Joue aux échecs vérifiables entre navigateurs, sans compte, sans publicité et sans serveur central.'
  },
  ja: {
    lang: 'ja',
    title: 'deChess — サーバのない、ピア同士のチェス',
    description: 'アカウントも広告も中央の対局サーバもなく、検証できるチェスをブラウザ同士で指せます。'
  }
};

export function isJapaneseRegionTimeZone(timeZone) {
  const zone = String(timeZone || '');
  return Boolean(zone) && JAPANESE_REGION_TIME_ZONES.has(zone);
}

export function isFrenchRegionTimeZone(timeZone) {
  const zone = String(timeZone || '');
  return Boolean(zone) && FRENCH_REGION_TIME_ZONES.has(zone);
}

export function isSpanishRegionTimeZone(timeZone) {
  const zone = String(timeZone || '');
  if (!zone) {
    return false;
  }
  if (zone.startsWith('America/Argentina/')) {
    return true;
  }
  return SPANISH_REGION_TIME_ZONES.has(zone);
}

export function readSystemTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

const BROWSER_LOCALES = new Set(['ja', 'es', 'fr']);

function readForcedLocale() {
  const value = globalThis.__DECHESS_FORCE_LOCALE__;
  return value === 'en' || value === 'es' || value === 'fr' || value === 'ja' ? value : null;
}

function readNavigatorLanguages() {
  try {
    const nav = globalThis.navigator;
    if (!nav) {
      return [];
    }
    if (Array.isArray(nav.languages) && nav.languages.length > 0) {
      return [...nav.languages];
    }
    return nav.language ? [nav.language] : [];
  } catch {
    return [];
  }
}

export function localeFromBrowserLanguages(languages) {
  const primary = String(languages?.[0] || '').toLowerCase().split('-')[0];
  return BROWSER_LOCALES.has(primary) ? primary : null;
}

export function resolveUiLocale({
  timeZone,
  forced,
  languages
} = {}) {
  const override = forced === undefined ? readForcedLocale() : forced;
  if (override === 'en' || override === 'es' || override === 'fr' || override === 'ja') {
    return override;
  }
  const browserLanguages = languages === undefined ? readNavigatorLanguages() : languages;
  const browserLocale = localeFromBrowserLanguages(browserLanguages);
  if (browserLocale === 'ja') {
    return 'ja';
  }
  const zone = timeZone === undefined ? readSystemTimeZone() : timeZone;
  if (isJapaneseRegionTimeZone(zone)) {
    return 'ja';
  }
  if (isFrenchRegionTimeZone(zone)) {
    return 'fr';
  }
  if (isSpanishRegionTimeZone(zone)) {
    return 'es';
  }
  return browserLocale || 'en';
}

export function applyDocumentLocale(locale) {
  const copy = DOCUMENT_COPY[locale] || DOCUMENT_COPY.en;
  if (typeof document === 'undefined') {
    return copy;
  }
  document.documentElement.lang = copy.lang;
  document.title = copy.title;
  const description = document.querySelector('meta[name="description"]');
  if (description) {
    description.setAttribute('content', copy.description);
  }
  const social = {
    'meta[property="og:title"]': copy.title,
    'meta[property="og:description"]': copy.description,
    'meta[name="twitter:title"]': copy.title,
    'meta[name="twitter:description"]': copy.description
  };
  for (const [selector, content] of Object.entries(social)) {
    document.querySelector(selector)?.setAttribute('content', content);
  }
  return copy;
}
